import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Elevation,
  Button,
  Tag,
  Spinner,
  NonIdealState,
  Callout,
  Icon,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  InputGroup,
  HTMLSelect,
  Drawer,
  Position,
  MenuItem,
  Divider,
  Tooltip,
} from "@blueprintjs/core";
import { Suggest } from "@blueprintjs/select";
import { api } from "../lib/api";
import type {
  BlueprintListItem,
  ConnectorCredit,
  ConnectorCreditsResponse,
  SuperadminUserItem,
  UserBlueprintAccess,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";

// ─────────── Helpers ───────────

function fmtRelative(iso: string | null): string {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return "Just now";
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min} min ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} mo ago`;
  return `${Math.round(months / 12)} yr ago`;
}

function statusIntent(s: string): "success" | "warning" | "danger" | "none" {
  if (s === "connected") return "success";
  if (s === "error") return "danger";
  if (s === "not_configured") return "warning";
  return "none";
}

function statusLabel(s: string): string {
  if (s === "connected") return "Connected";
  if (s === "error") return "Error";
  if (s === "not_configured") return "Not configured";
  return s;
}

function roleLabel(role: string): string {
  if (role === "superadmin") return "Administrator";
  if (role === "owner") return "Owner";
  if (role === "collaborator") return "Collaborator";
  if (role === "viewer") return "Viewer";
  if (role === "user") return "Member";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

// ─────────── Connector credits card ───────────

function ConnectorCreditTile({ c }: { c: ConnectorCredit }) {
  const intent = statusIntent(c.status);
  return (
    <div
      style={{
        flex: "1 1 240px",
        minWidth: 240,
        border: "1px solid #dce0e5",
        borderRadius: 6,
        padding: 14,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icon
          icon={c.icon as any}
          size={16}
          style={{ color: c.configured ? "#137cbd" : "#8a9ba8" }}
        />
        <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{c.name}</div>
        <Tag minimal round intent={intent} style={{ fontSize: 10 }}>
          {statusLabel(c.status)}
        </Tag>
      </div>
      {c.description && (
        <div style={{ fontSize: 12, color: "#5c7080", lineHeight: 1.4 }}>{c.description}</div>
      )}

      <div style={{ marginTop: 4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <div>
          <div style={{ fontSize: 10, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Remaining
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: c.remaining_credits != null ? "#137cbd" : "#5c7080" }}>
            {c.remaining_credits != null ? c.remaining_credits.toLocaleString() : "—"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Used (30 days)
          </div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {c.used_30d != null ? c.used_30d.toLocaleString() : "—"}
          </div>
        </div>
      </div>

      {(c.used_today != null || c.reveal_count_30d != null) && (
        <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#5c7080", flexWrap: "wrap" }}>
          {c.used_today != null && (
            <span>
              <strong>{c.used_today}</strong> credit{c.used_today === 1 ? "" : "s"} today
            </span>
          )}
          {c.reveal_count_30d != null && (
            <span>
              <strong>{c.reveal_count_30d}</strong> reveal{c.reveal_count_30d === 1 ? "" : "s"} / 30d
            </span>
          )}
        </div>
      )}

      {c.remaining_message && (
        <div style={{ fontSize: 11, color: "#8a9ba8", lineHeight: 1.4, marginTop: 4 }}>
          <Icon icon="info-sign" size={11} style={{ marginRight: 4, color: "#a7b6c2" }} />
          {c.remaining_message}
        </div>
      )}
      {c.message && c.status !== "connected" && (
        <div style={{ fontSize: 11, color: intent === "danger" ? "#c23030" : "#bf7326", lineHeight: 1.4, marginTop: 2 }}>
          {c.message}
        </div>
      )}
    </div>
  );
}

function ConnectorCreditsCard() {
  const [data, setData] = useState<ConnectorCreditsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = () => {
    setLoading(true);
    setError("");
    api.get<ConnectorCreditsResponse>("/superadmin/connector-credits")
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <Card elevation={Elevation.ONE} style={{ padding: 0, marginBottom: 20 }}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #dce0e5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Connector credits</h4>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#5c7080" }}>
            Live status, remaining balances, and usage for each integrated data source.
          </p>
        </div>
        <Button
          variant="minimal"
          icon="refresh"
          loading={loading}
          onClick={fetchData}
          title="Refresh balances"
        />
      </div>
      <div style={{ padding: 16 }}>
        {error && (
          <Callout intent="danger" style={{ marginBottom: 12 }}>
            {error}
          </Callout>
        )}
        {loading && !data ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
            <Spinner size={24} />
          </div>
        ) : data && data.connectors.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {data.connectors.map((c) => (
              <ConnectorCreditTile key={c.key} c={c} />
            ))}
          </div>
        ) : (
          <NonIdealState icon="cloud-download" title="No connectors configured" />
        )}
      </div>
    </Card>
  );
}

// ─────────── User detail drawer ───────────

interface UserDrawerProps {
  user: SuperadminUserItem | null;
  isOpen: boolean;
  onClose: () => void;
  onUserChanged: () => void;
  currentUserId: string | undefined;
}

function UserDrawer({ user, isOpen, onClose, onUserChanged, currentUserId }: UserDrawerProps) {
  const [bps, setBps] = useState<UserBlueprintAccess[]>([]);
  const [allBps, setAllBps] = useState<BlueprintListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [grantQuery, setGrantQuery] = useState("");
  const [grantRole, setGrantRole] = useState<"collaborator" | "viewer" | "owner">("collaborator");
  const [granting, setGranting] = useState(false);

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const [list, all] = await Promise.all([
        api.get<UserBlueprintAccess[]>(`/superadmin/users/${user.id}/blueprints`),
        api.get<BlueprintListItem[]>("/blueprints"),
      ]);
      setBps(list);
      setAllBps(all);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      setSuccess("");
      setError("");
      setGrantQuery("");
      setGrantRole("collaborator");
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id]);

  const grantableBps = useMemo(() => {
    if (!user) return [];
    if (user.role === "superadmin") return []; // already has access to all.
    const accessibleIds = new Set(bps.map((b) => b.blueprint_id));
    return allBps.filter((b) => !accessibleIds.has(b.id) && !b.is_archived);
  }, [user, bps, allBps]);

  const handleGrant = async (bp: BlueprintListItem) => {
    if (!user) return;
    setGranting(true);
    setError("");
    setSuccess("");
    try {
      await api.post(`/superadmin/users/${user.id}/blueprints/${bp.id}`, { role: grantRole });
      setSuccess(`Granted ${roleLabel(grantRole).toLowerCase()} access to "${bp.name}".`);
      setGrantQuery("");
      await refresh();
      onUserChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (bp: UserBlueprintAccess) => {
    if (!user) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/superadmin/users/${user.id}/blueprints/${bp.blueprint_id}`);
      setSuccess(`Removed access to "${bp.name}".`);
      await refresh();
      onUserChanged();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleRoleChange = async (bp: UserBlueprintAccess, newRole: string) => {
    if (!user || newRole === bp.role) return;
    setError("");
    setSuccess("");
    try {
      await api.patch(`/superadmin/users/${user.id}/blueprints/${bp.blueprint_id}`, { role: newRole });
      await refresh();
      onUserChanged();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleToggleAdmin = async () => {
    if (!user) return;
    const newRole = user.role === "superadmin" ? "user" : "superadmin";
    setError("");
    setSuccess("");
    try {
      await api.patch(`/superadmin/users/${user.id}/role`, { role: newRole });
      setSuccess(
        newRole === "superadmin"
          ? `${user.full_name} is now an administrator.`
          : `${user.full_name} is now a regular member.`
      );
      onUserChanged();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    setError("");
    setSuccess("");
    try {
      await api.post(`/auth/users/${user.id}/reset-password`);
      setSuccess(`Password reset email sent to ${user.email}.`);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleToggleActive = async () => {
    if (!user) return;
    setError("");
    setSuccess("");
    try {
      if (user.is_active) {
        await api.patch(`/auth/users/${user.id}/disable`);
      } else {
        await api.patch(`/auth/users/${user.id}/enable`);
      }
      onUserChanged();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (!user) return null;

  const isSelf = currentUserId === user.id;
  const initials = user.full_name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position={Position.RIGHT}
      size={580}
      title={null}
      icon={null}
      hasBackdrop
      isCloseButtonShown={false}
    >
      {/* Custom header — keeps layout tidy and matches the rest of the app */}
      <div
        style={{
          padding: "18px 20px",
          borderBottom: "1px solid #dce0e5",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "#137cbd",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{user.full_name}</div>
          <div style={{ fontSize: 13, color: "#5c7080", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user.email}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <Tag minimal round intent={user.role === "superadmin" ? "primary" : "none"} style={{ fontSize: 10 }}>
              {roleLabel(user.role)}
            </Tag>
            <Tag minimal round intent={user.is_active ? "success" : "danger"} style={{ fontSize: 10 }}>
              {user.is_active ? "Active" : "Disabled"}
            </Tag>
          </div>
        </div>
        <Button variant="minimal" icon="cross" onClick={onClose} />
      </div>

      <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
        {error && (
          <Callout intent="danger" style={{ marginBottom: 12 }}>
            {error}
          </Callout>
        )}
        {success && (
          <Callout intent="success" icon="tick-circle" style={{ marginBottom: 12 }}>
            {success}
          </Callout>
        )}

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
          <div style={{ padding: 10, border: "1px solid #dce0e5", borderRadius: 4 }}>
            <div style={{ fontSize: 10, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Blueprints
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{user.blueprint_count}</div>
          </div>
          <div style={{ padding: 10, border: "1px solid #dce0e5", borderRadius: 4 }}>
            <div style={{ fontSize: 10, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Credits / 30d
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{user.credits_used_30d}</div>
          </div>
          <div style={{ padding: 10, border: "1px solid #dce0e5", borderRadius: 4 }}>
            <div style={{ fontSize: 10, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Last login
            </div>
            <div style={{ fontSize: 13, fontWeight: 500 }} title={user.last_login_at ?? "Never"}>
              {fmtRelative(user.last_login_at)}
            </div>
          </div>
        </div>

        {/* Account actions */}
        <div style={{ marginBottom: 20 }}>
          <h5 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#5c7080", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Account
          </h5>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              icon="key"
              size="small"
              onClick={handleResetPassword}
              disabled={isSelf}
              title={isSelf ? "Use the password change flow for your own account." : ""}
            >
              Reset password
            </Button>
            <Button
              icon={user.role === "superadmin" ? "user" : "shield"}
              size="small"
              intent={user.role === "superadmin" ? undefined : "primary"}
              onClick={handleToggleAdmin}
              disabled={isSelf}
              title={isSelf ? "You can't change your own role." : ""}
            >
              {user.role === "superadmin" ? "Demote to member" : "Make administrator"}
            </Button>
            <Button
              icon={user.is_active ? "disable" : "tick-circle"}
              size="small"
              intent={user.is_active ? "danger" : "success"}
              onClick={handleToggleActive}
              disabled={isSelf}
              title={isSelf ? "You can't disable your own account." : ""}
            >
              {user.is_active ? "Disable account" : "Enable account"}
            </Button>
          </div>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* Blueprint access */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <h5 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#5c7080", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Blueprint access
            </h5>
            <span style={{ fontSize: 11, color: "#8a9ba8" }}>
              {bps.length} blueprint{bps.length === 1 ? "" : "s"}
            </span>
          </div>

          {user.role === "superadmin" ? (
            <Callout intent="primary" icon="shield" style={{ marginBottom: 12 }}>
              Administrators automatically have access to every blueprint in the workspace.
            </Callout>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 12,
                padding: 10,
                border: "1px solid #dce0e5",
                borderRadius: 4,
                background: "#fafbfc",
              }}
            >
              <div style={{ flex: 1, position: "relative" }}>
                <Suggest<BlueprintListItem>
                  items={grantableBps}
                  itemPredicate={(q, item) =>
                    item.name.toLowerCase().includes(q.toLowerCase()) ||
                    (item.description ?? "").toLowerCase().includes(q.toLowerCase())
                  }
                  itemRenderer={(bp, { handleClick, modifiers }) =>
                    !modifiers.matchesPredicate ? null : (
                      <MenuItem
                        key={bp.id}
                        text={bp.name}
                        label={bp.status}
                        onClick={handleClick}
                        active={modifiers.active}
                        roleStructure="listoption"
                      />
                    )
                  }
                  inputValueRenderer={(bp) => bp.name}
                  onItemSelect={handleGrant}
                  query={grantQuery}
                  onQueryChange={setGrantQuery}
                  noResults={
                    <MenuItem
                      disabled
                      text={
                        grantableBps.length === 0
                          ? "User already has access to every blueprint."
                          : "No blueprints match."
                      }
                    />
                  }
                  popoverProps={{
                    minimal: true,
                    usePortal: false,
                    matchTargetWidth: true,
                    placement: "bottom-start",
                  }}
                  inputProps={{
                    placeholder: "Grant access to a blueprint…",
                    leftIcon: "search",
                  }}
                  disabled={granting}
                  resetOnSelect
                />
              </div>
              <HTMLSelect
                value={grantRole}
                onChange={(e) => setGrantRole(e.target.value as any)}
                disabled={granting}
                style={{ width: 140 }}
              >
                <option value="collaborator">Collaborator</option>
                <option value="viewer">Viewer</option>
                <option value="owner">Owner</option>
              </HTMLSelect>
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <Spinner size={20} />
            </div>
          ) : bps.length === 0 ? (
            <div style={{ padding: 20 }}>
              <NonIdealState
                icon="map-create"
                title="No blueprint access"
                description="Use the field above to grant access to a blueprint."
              />
            </div>
          ) : (
            <table className="lead-table">
              <thead>
                <tr>
                  <th>Blueprint</th>
                  <th>Role</th>
                  <th>Granted</th>
                  <th style={{ textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {bps.map((bp) => (
                  <tr key={bp.blueprint_id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{bp.name}</div>
                      <div style={{ fontSize: 11, color: "#8a9ba8" }}>
                        {bp.status} · {bp.lead_count} lead{bp.lead_count === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td>
                      {bp.role === "superadmin" ? (
                        <Tooltip content="Access via administrator role.">
                          <Tag minimal round intent="primary" icon="shield" style={{ fontSize: 10 }}>
                            Admin
                          </Tag>
                        </Tooltip>
                      ) : (
                        <HTMLSelect
                          minimal
                          value={bp.role}
                          onChange={(e) => handleRoleChange(bp, e.target.value)}
                          style={{ fontSize: 12 }}
                        >
                          <option value="owner">Owner</option>
                          <option value="collaborator">Collaborator</option>
                          <option value="viewer">Viewer</option>
                        </HTMLSelect>
                      )}
                    </td>
                    <td style={{ fontSize: 11, color: "#5c7080" }}>
                      {bp.added_at ? fmtRelative(bp.added_at) : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {bp.role !== "superadmin" && (
                        <Tooltip content="Remove access">
                          <Button
                            size="small"
                            variant="minimal"
                            icon="cross"
                            intent="danger"
                            onClick={() => handleRevoke(bp)}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Drawer>
  );
}

// ─────────── Main page ───────────

export function Settings() {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";

  const [users, setUsers] = useState<SuperadminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("user");

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "superadmin" | "user">("all");
  const [selected, setSelected] = useState<SuperadminUserItem | null>(null);

  const fetchUsers = () => {
    if (!isSuperadmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get<SuperadminUserItem[]>("/superadmin/users")
      .then((list) => {
        setUsers(list);
        setSelected((prev) => (prev ? list.find((u) => u.id === prev.id) ?? null : null));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    if (!email.trim() || !fullName.trim()) {
      setFormError("Email and full name are required");
      return;
    }
    setCreating(true);
    setFormError("");
    setSuccessMsg("");
    try {
      const res = await api.post<{ message: string }>("/auth/users", {
        email: email.trim(),
        full_name: fullName.trim(),
        role,
      });
      setSuccessMsg(res.message);
      setShowCreate(false);
      setEmail("");
      setFullName("");
      setRole("user");
      fetchUsers();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);

  if (!isSuperadmin) {
    return (
      <div>
        <div className="dash-header">
          <div>
            <h2>Settings</h2>
            <p>Workspace configuration</p>
          </div>
        </div>
        <NonIdealState
          icon="lock"
          title="Administrator access required"
          description="Only administrators can manage workspace settings, user accounts, and connector credits."
        />
      </div>
    );
  }

  return (
    <div>
      <div className="dash-header">
        <div>
          <h2>Admin</h2>
          <p>Manage users, blueprint access, and connector credits</p>
        </div>
        <div className="dash-header-actions">
          <Button icon="new-person" intent="primary" onClick={() => setShowCreate(true)}>
            Create user
          </Button>
        </div>
      </div>

      {error && (
        <Callout intent="danger" style={{ marginBottom: 16 }}>
          {error}
        </Callout>
      )}
      {successMsg && (
        <Callout intent="success" style={{ marginBottom: 16 }} icon="tick-circle">
          {successMsg}
        </Callout>
      )}

      <ConnectorCreditsCard />

      <Card elevation={Elevation.ONE} style={{ padding: 0 }}>
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #dce0e5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Team members</h4>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#5c7080" }}>
              Click a user to view and manage their blueprint access.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <InputGroup
              leftIcon="search"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              small
              style={{ width: 220 }}
            />
            <HTMLSelect
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              minimal
            >
              <option value="all">All roles</option>
              <option value="superadmin">Administrators</option>
              <option value="user">Members</option>
            </HTMLSelect>
          </div>
        </div>
        <div className="dash-panel-body no-pad">
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Spinner size={28} />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: 40 }}>
              <NonIdealState icon="people" title="No users match" />
            </div>
          ) : (
            <table className="lead-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Blueprints</th>
                  <th>Credits / 30d</th>
                  <th>Last login</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setSelected(u)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ fontWeight: 500 }}>{u.full_name}</td>
                    <td style={{ fontSize: 13, color: "#5c7080" }}>{u.email}</td>
                    <td>
                      <Tag minimal round intent={u.role === "superadmin" ? "primary" : "none"}>
                        {roleLabel(u.role)}
                      </Tag>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {u.blueprint_count}
                      {u.role === "superadmin" && (
                        <span style={{ fontSize: 10, color: "#8a9ba8", marginLeft: 4 }}>
                          (all)
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>{u.credits_used_30d}</td>
                    <td style={{ fontSize: 12, color: "#5c7080" }}>{fmtRelative(u.last_login_at)}</td>
                    <td>
                      <Tag minimal round intent={u.is_active ? "success" : "danger"}>
                        {u.is_active ? "Active" : "Disabled"}
                      </Tag>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Icon icon="chevron-right" size={14} style={{ color: "#a7b6c2" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <UserDrawer
        user={selected}
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        onUserChanged={fetchUsers}
        currentUserId={currentUser?.id}
      />

      <Dialog
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create user account"
        style={{ width: 480 }}
      >
        <DialogBody>
          {formError && <Callout intent="danger" style={{ marginBottom: 16 }}>{formError}</Callout>}
          <p style={{ margin: "0 0 20px", fontSize: 13, color: "#5c7080" }}>
            The user will receive an email with temporary login credentials from Criterion.
          </p>
          <FormGroup label="Full name" labelInfo="(required)">
            <InputGroup
              placeholder="John Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Email" labelInfo="(required)">
            <InputGroup
              type="email"
              placeholder="john@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Role">
            <HTMLSelect fill value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="user">Member</option>
              <option value="superadmin">Administrator</option>
            </HTMLSelect>
          </FormGroup>
        </DialogBody>
        <DialogFooter
          actions={
            <>
              <Button onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button intent="primary" onClick={handleCreate} loading={creating}>
                Create &amp; send invite
              </Button>
            </>
          }
        />
      </Dialog>
    </div>
  );
}
